<template>
  <div class="module">
    <router-link v-if="!isHostEmbed" :to="event ? eventPath(event) : '/'" class="lb-back-link">
      <i class="fa-solid fa-arrow-left"></i> {{ event ? "Retour à l'événement" : 'Retour au calendrier' }}
    </router-link>

    <div v-if="loadingEvent" class="lb-loading">Chargement...</div>
    <p v-else-if="!event" class="lb-loading">Événement introuvable.</p>

    <template v-else>
      <div class="event-header">
        <h1 class="page-title">Classement - {{ displayEventName(event) }}</h1>
        <div class="event-meta">
          <span><i class="fa-solid fa-calendar" /> {{ formatEventDateRange(event.date, event.date_end) }}</span>
          <span v-if="event.place"><i class="fa-solid fa-location-dot" /> {{ titleCase(event.place) }}</span>
        </div>
      </div>

      <!-- Visiteur anonyme : liste brute (rang, joueur sans code club/comité, points),
      sans filtres, recherche ni export. -->
      <template v-if="!authStore.isAuthenticated">
        <LoginPrompt message="Connectez-vous pour filtrer le classement, rechercher un joueur et voir les clubs." />

        <div v-if="loading" class="lb-loading">Chargement du classement...</div>
        <div v-else-if="error" class="error">{{ error }}</div>
        <div v-else-if="!hasRankingEntries(leaderboard)" class="lb-loading">
          Aucun classement disponible pour ce tournoi.
        </div>
        <div v-for="category in leaderboard" v-else :key="category.category">
          <template v-if="category.entries.length">
            <h3>{{ formatCategory(category.category) }}</h3>
            <table class="leaderboardtable">
              <thead>
                <tr>
                  <th></th>
                  <th>Joueur</th>
                  <th>PTS</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(entry, index) in category.entries" :key="index">
                  <td>{{ entry.clt }}</td>
                  <td>{{ stripEntityCodes(entry.joueur) }}</td>
                  <td>{{ entry.pts }}</td>
                </tr>
              </tbody>
            </table>
          </template>
        </div>
      </template>

      <template v-else>
        <div class="filters">
          <label for="category-select">Catégorie :</label>
          <select id="category-select" v-model="selectedCategory">
            <option value="">Toutes</option>
            <option v-for="category in leaderboard" :value="category.category" :key="category.category">
              {{ formatCategory(category.category) }}
            </option>
          </select>

          <input v-model="searchQuery" type="text" placeholder="Rechercher un joueur…" class="search-input" />

          <label class="checkbox-inline">
            <input v-model="onlyCurrentUser" type="checkbox" class="styled-checkbox" />
            Mes résultats uniquement
          </label>
        </div>

        <div v-if="canExport" class="lb-export-buttons no-print">
          <button :disabled="!hasRankingEntries(leaderboard)" @click="exportPdf">
            <i class="fa-solid fa-file-pdf"></i> Exporter en PDF
          </button>
        </div>

        <div v-if="loading" class="lb-loading">Chargement du classement...</div>
        <div v-else-if="error" class="error">{{ error }}</div>
        <div v-else-if="!hasRankingEntries(leaderboard)" class="lb-loading">
          Aucun classement disponible pour ce tournoi.
        </div>

        <div v-else-if="!visibleCategories.length" class="lb-loading">Aucune entrée dans ce classement.</div>
        <div v-for="category in visibleCategories" v-else :key="category.category">
          <h3>{{ formatCategory(category.category) }}</h3>
          <table class="leaderboardtable">
            <thead>
              <tr>
                <th></th>
                <th>Joueur</th>
                <th>PTS</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(entry, index) in category.entries" :key="index">
                <td>{{ entry.clt }}</td>
                <td :class="{ 'current-user': authStore.isCurrentUser_leaderboard(entry.joueur) }">{{ entry.joueur }}</td>
                <td>{{ entry.pts }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import backendApi from '@/axios/backendApi';
import { useAuthStore } from '@/stores/useAuthStore';
import { formatCategory, formatEventDateRange, titleCase } from '@/functions/utils';
import {
  displayEventName, eventPath, eventSlug, parseEventId, toOfficialEvent,
} from '@/functions/eventDisplay';
import { canExportLeaderboard, hasRankingEntries, stripEntityCodes, type RankingCategory } from '@/functions/leaderboards';
import { applySeo } from '@/functions/seo';
import { exportLeaderboardPdf } from '@/functions/leaderboardPdf';
import LoginPrompt from '@/components/LoginPrompt.vue';
import { isHostEmbed } from '@/embedHost';

const route = useRoute();
const authStore = useAuthStore();
const canExport = computed(() => canExportLeaderboard(authStore.scopes));

const eventId = computed(() => parseEventId(route.params.eventId));
const event = ref<OfficialEvent | null>(null);
const loadingEvent = ref(true);

const leaderboard = ref<RankingCategory[]>([]);
const loading = ref(false);
const error = ref('');

const selectedCategory = ref('');
const onlyCurrentUser = ref(false);
const searchQuery = ref('');

const normalize = (s: string) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

const filteredCategories = computed(() => {
  const q = searchQuery.value.trim() ? normalize(searchQuery.value.trim()) : '';
  return leaderboard.value
    .filter((cat) => !selectedCategory.value || cat.category === selectedCategory.value)
    .map((cat) => ({
      ...cat,
      entries: cat.entries
        .filter((e) => !q || normalize(e.joueur).includes(q))
        .filter((e) => !onlyCurrentUser.value || authStore.isCurrentUser_leaderboard(e.joueur)),
    }));
});

// Catégories vides (ex. "Mes résultats uniquement") masquées plutôt qu'affichées vides.
const visibleCategories = computed(() => filteredCategories.value.filter(cat => cat.entries.length > 0));

const exportPdf = () => {
  if (!event.value) return;
  const ev = event.value;
  exportLeaderboardPdf({
    title: `Classement - ${displayEventName(ev)}`,
    subtitle: [formatEventDateRange(ev.date, ev.date_end), ev.place && titleCase(ev.place)].filter(Boolean).join(' - '),
    head: ['#', 'Joueur', 'PTS'],
    categories: filteredCategories.value.map((cat) => ({
      name: formatCategory(cat.category),
      rows: cat.entries.map((e) => [e.clt ?? '', e.joueur, e.pts ?? '']),
    })),
    filename: `classement-${eventSlug(ev)}.pdf`,
  });
};

const fetchLeaderboard = async () => {
  if (!event.value) return;
  loading.value = true;
  error.value = '';
  try {
    const { data } = await backendApi.get(`/leaderboard/event/${event.value.id}`);
    leaderboard.value = data.leaderboard || [];
  } catch {
    error.value = 'Échec du chargement du classement.';
    leaderboard.value = [];
  } finally {
    loading.value = false;
  }
};

// Déconnexion : filtres masqués pour un anonyme, ils ne doivent plus s'appliquer.
watch(() => authStore.isAuthenticated, (auth) => {
  if (auth) return;
  selectedCategory.value = '';
  searchQuery.value = '';
  onlyCurrentUser.value = false;
});

onMounted(async () => {
  if (isNaN(eventId.value)) {
    loadingEvent.value = false;
    return;
  }
  try {
    const { data } = await backendApi.get(`/events/${eventId.value}`);
    event.value = toOfficialEvent(data);
    // Miroir de Dartshub events/seo_head.py::event_head(page="classement")
    const ev = event.value;
    const place = ev.place ? titleCase(ev.place) : null;
    const summary = [`Tournoi de fléchettes le ${formatEventDateRange(ev.date, ev.date_end)}`, place && `à ${place}`,
      ev.organiser && `organisé par ${ev.organiser}`].filter(Boolean).join(' ');
    applySeo({
      title: `Classement - ${displayEventName(ev)}`,
      description: `Résultats et points au classement. ${summary}.`,
      path: `${eventPath(ev)}/classement`,
    });
  } catch {
    event.value = null;
  } finally {
    loadingEvent.value = false;
  }
  fetchLeaderboard();
});
</script>

<style scoped>
.event-header {
  margin-bottom: 1.5rem;
}

.event-meta {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
}

.event-meta span {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.85rem;
  color: var(--color-fg-darker);
}
</style>
